import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, Clock, CheckCircle2, XCircle, UserX, Activity } from "lucide-react";

const CARDS = [
  { k: "today", label: "Today", color: "bg-[#0F4C81]/10 text-[#0F4C81]", icon: Activity, tid: "dashboard-stat-today-count" },
  { k: "upcoming", label: "Upcoming", color: "bg-sky-100 text-sky-700", icon: CalendarDays, tid: "dashboard-stat-upcoming-count" },
  { k: "pending", label: "Pending", color: "bg-amber-100 text-amber-800", icon: Clock, tid: "dashboard-stat-pending-count" },
  { k: "confirmed", label: "Confirmed", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, tid: "dashboard-stat-confirmed-count" },
  { k: "completed", label: "Completed", color: "bg-indigo-100 text-indigo-800", icon: CheckCircle2, tid: "dashboard-stat-completed-count" },
  { k: "cancelled", label: "Cancelled", color: "bg-rose-100 text-rose-800", icon: XCircle, tid: "dashboard-stat-cancelled-count" },
  { k: "no_show", label: "No-show", color: "bg-slate-200 text-slate-700", icon: UserX, tid: "dashboard-stat-noshow-count" },
];

export default function DashboardOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-500">Loading dashboard...</div>;
  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div>
        <h1 className="text-2xl font-display font-bold text-slate-900">Overview</h1>
        <p className="text-slate-500 text-sm">Snapshot of your clinic's day</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {CARDS.map((c) => (
          <div key={c.k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={c.tid}>
            <div className={`w-9 h-9 rounded-lg grid place-items-center ${c.color}`}><c.icon size={16} /></div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-3">{data[c.k] ?? 0}</div>
            <div className="text-xs text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-slate-900">Today's schedule</div>
            <div className="text-xs text-slate-500">All appointments for today</div>
          </div>
          <Link to="/doctor/appointments" className="text-sm text-emerald-700 font-semibold hover:text-emerald-800">View all →</Link>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          {(data.today_appointments || []).length === 0 && <div className="text-sm text-slate-500 py-4">No appointments today.</div>}
          {(data.today_appointments || []).map((a) => (
            <div key={a.id} className="py-3 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="font-mono font-semibold text-slate-800">{a.start_time}</div>
                <div>
                  <div className="font-medium text-slate-900">{a.patient_name}</div>
                  <div className="text-xs text-slate-500">{a.consultation_type} · {a.reference}</div>
                </div>
              </div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    rescheduled: "bg-sky-100 text-sky-800",
    cancelled: "bg-rose-100 text-rose-800",
    completed: "bg-indigo-100 text-indigo-800",
    no_show: "bg-slate-200 text-slate-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100 text-slate-700"}`}>{(status || "").replace("_", " ")}</span>;
}
